import { environment } from '@accredible-frontend-v2/envs';
import { AccredibleAccountsRedirectionServiceModule } from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleBrowserStorageServiceModule } from '@accredible-frontend-v2/services/browser-storage';
import { AccredibleCookiesServiceModule } from '@accredible-frontend-v2/services/cookies';
import { AccredibleLanguageServiceModule } from '@accredible-frontend-v2/services/language';
import { AccredibleUserServiceModule } from '@accredible-frontend-v2/services/user';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Routes, withInMemoryScrolling } from '@angular/router';
import { ShellComponent } from '@ler/feature-layout';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { lerAuthGuard } from './core/ler-auth.guard';
import { lerLandingPageRedirectGuard } from './core/ler-landing-page-redirect.guard';
import { LerLoadService } from './core/ler-load.service';
import { lerTermsGuard } from './core/ler-terms.guard';

const routes: Routes = [
  {
    path: 'ler-terms',
    loadComponent: () => import('@ler/terms').then((m) => m.LerTermsPageComponent),
  },
  {
    path: '',
    canActivate: [lerLandingPageRedirectGuard],
    children: [
      {
        path: 'speak-your-skills/:credentialId',
        loadChildren: () =>
          import('@ler/feature-speak-your-skills').then((m) => m.SpeakYourSkillsRoutes),
      },
      {
        path: 'career-opportunities/:credentialId',
        loadChildren: () => import('@ler/feature-jobs').then((m) => m.CareerOpportunitiesRoutes),
      },
      {
        path: 'next-steps/:credentialId',
        loadChildren: () => import('@ler/feature-journey').then((m) => m.NextStepsRoutes),
      },
    ],
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [lerAuthGuard],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'accept-terms',
        loadComponent: () => import('@ler/terms').then((m) => m.LerAcceptTermsContainer),
      },
      {
        path: '',
        canActivate: [lerTermsGuard],
        children: [
          {
            path: 'dashboard',
            loadChildren: () => import('@ler/feature-dashboard').then((m) => m.DashboardRoutes),
          },
          {
            path: 'set-your-goal',
            loadChildren: () =>
              import('@ler/feature-set-your-goal').then((m) => m.SetYourGoalRoutes),
          },
          {
            path: 'jobs',
            loadChildren: () => import('@ler/feature-jobs').then((m) => m.JobsRoutes),
          },
        ],
      },
      {
        path: 'journey',
        loadChildren: () => import('@ler/feature-journey').then((m) => m.JourneyRoutes),
      },
    ],
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    // LerCredentialFacade depends on CredentialsService,
    // RecaptchaV3Module is required because CredentialsService depends on AccredibleRecaptchaService,
    // which injects ReCaptchaV3Service from ng-recaptcha.
    // We should have a dedicated service for LerCredentials that doesn't depend on RecaptchaV3Module.
    importProvidersFrom(
      RecaptchaV3Module,
      AccredibleBrowserStorageServiceModule,
      AccredibleCookiesServiceModule,
      AccredibleUserServiceModule,
      AccredibleAccountsRedirectionServiceModule,
      AccredibleLanguageServiceModule.forRoot(['en']),
    ),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaV3SiteKey,
    },
    LerLoadService,
    {
      provide: APP_INITIALIZER,
      useFactory: (loadService: LerLoadService) => () => loadService.initializeApp(),
      deps: [LerLoadService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (loadService: LerLoadService) => () => loadService.validateAuthenticationToken(),
      deps: [LerLoadService],
      multi: true,
    },
  ],
};
